import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { RequireCompletedInvite } from '@/components/common/RequireCompletedInvite';
import { AdminRoute } from '@/components/admin/AdminRoute';
import { AdminLayout } from '@/components/admin/AdminLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { isPortalSubdomain, isCustomDomain } from '@/utils/portalDetection';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const TestRegisterPage = lazy(() => import('@/pages/TestRegisterPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const ConfirmEmailChangePage = lazy(
  () => import('@/pages/auth/ConfirmEmailChangePage')
);
const VarioInstallPage = lazy(() => import('@/pages/vario/VarioInstallPage'));
const VarioUninstallPage = lazy(() => import('@/pages/vario/VarioUninstallPage'));
const DashboardPage = lazy(() => import('@/pages/merchant/DashboardPage'));
// ReturnsListPage + ReturnDetailPage replaced by AnfragenListPage + AnfrageDetailPage
const PortalSettingsPage = lazy(() => import('@/pages/PortalSettingsPage'));
const PortalDetailPage = lazy(() => import('@/pages/PortalDetailPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage'));
const ProfilePage = lazy(() => import('@/pages/ProfileSettingsPage'));
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage'));
const DeliveriesPage = lazy(() => import('@/pages/DeliveriesPage'));
const DeliveryDetailPage = lazy(() => import('@/pages/DeliveryDetailPage'));
const GeneralSettingsPage = lazy(() => import('@/pages/GeneralSettingsPage'));
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const WarehouseQuickReceiptPage = lazy(() => import('@/pages/WarehouseQuickReceiptPage'));
const JobsPage = lazy(() => import('@/pages/JobsPage'));
const AnfragenListPage = lazy(() => import('@/pages/AnfragenListPage'));
const AnfrageDetailPage = lazy(() => import('@/pages/AnfrageDetailPage'));

// Portal pages (End Customer Flow)
const LandingPage = lazy(() => import('@/pages/portal/LandingPage'));
const OrderFoundPage = lazy(() => import('@/pages/portal/OrderFoundPage'));
const ReturnReasonPage = lazy(() => import('@/pages/portal/ReturnReasonPage'));
const ReviewPage = lazy(() => import('@/pages/portal/ReviewPage'));
const SuccessPage = lazy(() => import('@/pages/portal/SuccessPage'));
const TrackReturnPage = lazy(() => import('@/pages/portal/TrackReturnPage'));

const WorkflowsPage = lazy(() => import('@/pages/WorkflowsPage'));
const WebhooksPage = lazy(() => import('@/pages/WebhooksPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Admin pages
// AdminLoginPage unused - admin access via go-ecommerce.de email
const AdminDashboardPage = lazy(
  () => import('@/pages/admin/AdminDashboardPage')
);
const MerchantsListPage = lazy(() => import('@/pages/admin/MerchantsListPage'));
const MerchantDetailPage = lazy(
  () => import('@/pages/admin/MerchantDetailPage')
);
const AuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'));
const KBQuestionsPage = lazy(() => import('@/pages/admin/KBQuestionsPage'));
const PricingManagementPage = lazy(
  () => import('@/pages/admin/PricingManagementPage')
);
const SetupWizardPage = lazy(() => import('@/pages/admin/SetupWizardPage'));
const WikiArticlesPage = lazy(() => import('@/pages/admin/WikiArticlesPage'));
const ChangelogPage = lazy(() => import('@/pages/admin/ChangelogPage'));
const ExsLicensesPage = lazy(() => import('@/pages/admin/ExsLicensesPage'));
const OAuthConsentPage = lazy(() => import('@/pages/admin/OAuthConsentPage'));

const portalSubdomain = isPortalSubdomain() || isCustomDomain();

export const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {portalSubdomain ? (
          <>
            {/* Subdomain Portal Routes (e.g. sabro.retoure.shop/) */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/return/select" element={<OrderFoundPage />} />
            <Route path="/return/reason" element={<ReturnReasonPage />} />
            <Route path="/return/review" element={<ReviewPage />} />
            <Route
              path="/return/success/:returnNumber"
              element={<SuccessPage />}
            />
            <Route path="/track" element={<Navigate to="/" replace />} />
            <Route path="/track/:returnNumber" element={<TrackReturnPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/test-register" element={<TestRegisterPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/auth/confirm-email-change"
              element={<ConfirmEmailChangePage />}
            />

            {/* Vario-Embedding: Install/Uninstall-Slots (oeffentlich, appToken-basiert,
                dialogfrei - keine Credentials noetig) */}
            <Route path="/vario/install" element={<VarioInstallPage />} />
            <Route path="/vario/uninstall" element={<VarioUninstallPage />} />

            {/* OAuth-Consent (Support-MCP): eigenstaendige Vollbild-Seite ohne
                AdminLayout. Top-Level, damit die Browser-URL mit basename /admin
                exakt /admin/oauth-consent ist (= MCP_CONSENT_PAGE_URL). */}
            <Route
              path="/oauth-consent"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <OAuthConsentPage />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />

            {/* Portal Routes (End Customer Flow) */}
            <Route path="/portal/:portalSlug" element={<LandingPage />} />
            <Route
              path="/portal/:portalSlug/return/select"
              element={<OrderFoundPage />}
            />
            <Route
              path="/portal/:portalSlug/return/reason"
              element={<ReturnReasonPage />}
            />
            <Route
              path="/portal/:portalSlug/return/review"
              element={<ReviewPage />}
            />
            <Route
              path="/portal/:portalSlug/return/success/:returnNumber"
              element={<SuccessPage />}
            />
            <Route
              path="/portal/:portalSlug/track"
              element={<TrackReturnPage />}
            />
            <Route
              path="/portal/:portalSlug/track/:returnNumber"
              element={<TrackReturnPage />}
            />

            {/* Protected Merchant Routes with Dashboard Layout */}
            <Route
              element={
                <ProtectedRoute>
                  <RequireCompletedInvite>
                    <DashboardLayout />
                  </RequireCompletedInvite>
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              {/* Old /returns routes removed - unified with AnfragenListPage */}
              <Route path="/portal/settings" element={<PortalSettingsPage />} />
              <Route
                path="/portal/settings/:portalId"
                element={<PortalDetailPage />}
              />
              <Route
                path="/portal/settings/:portalId/:section"
                element={<PortalDetailPage />}
              />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/warehouse/quick-receipt" element={<WarehouseQuickReceiptPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/jobs/:jobId" element={<JobsPage />} />
              <Route path="/deliveries" element={<DeliveriesPage />} />
              <Route path="/deliveries/:id" element={<DeliveryDetailPage />} />
              <Route path="/returns" element={<AnfragenListPage />} />
              <Route path="/returns/:uid" element={<AnfrageDetailPage />} />
              <Route
                path="/settings/global"
                element={<GeneralSettingsPage />}
              />
              <Route
                path="/settings/global/:section"
                element={<GeneralSettingsPage />}
              />
              <Route path="/settings/workflows" element={<WorkflowsPage />} />
              <Route
                path="/settings/workflows/:workflowId"
                element={<WorkflowsPage />}
              />
              <Route path="/settings/webhooks" element={<WebhooksPage />} />
              <Route
                path="/transactions"
                element={<Navigate to="/subscription" replace />}
              />
            </Route>

            {/* Admin Routes (own layout with sidebar, outside DashboardLayout) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="merchants" element={<MerchantsListPage />} />
              <Route
                path="merchants/:merchantId"
                element={<MerchantDetailPage />}
              />
              <Route path="audit" element={<AuditLogsPage />} />
              <Route path="wiki" element={<WikiArticlesPage />} />
              <Route path="kb" element={<KBQuestionsPage />} />
              <Route path="setup-wizard" element={<SetupWizardPage />} />
              <Route path="pricing" element={<PricingManagementPage />} />
              <Route path="changelog" element={<ChangelogPage />} />
              <Route path="exs-lizenzen" element={<ExsLicensesPage />} />
            </Route>

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
};
